/**
 * HistoryShapedPanel — draft generation SHAPED BY THIS AUTHOR'S HISTORY,
 * with provenance receipts and ground-truth score calibration.
 *
 * Thin view over three PURE engines:
 *   • lib/content/playbookLoader  — structured field-study rules + retrieval
 *   • lib/content/promptShaper    — history-blended prompts, provenance
 *                                   receipts, worker-or-fallback generation
 *   • lib/content/scoreCalibration — personal-signal-adjusted scoring,
 *                                   lowest-dimension rewrite target, and
 *                                   predicted-vs-actual backtesting
 *
 * The Generate button calls `generateWithWorker` — the REAL worker seam
 * (lib/agent/argoWorker) when configured, streaming the returned text into
 * the editor; the deterministic template fallback otherwise, labelled
 * honestly. The "signals used" chip is rendered from `toPromptProvenance`,
 * naming exactly which signals + rules shaped the draft. "Rewrite lowest"
 * is driven by `lowestDimension`, and the calibration mini panel comes from
 * `backtest` over the session's logged posts.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Fingerprint,
  History,
  Loader2,
  Sparkles,
  Wand2,
  Activity,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import * as argoWorker from '../lib/agent/argoWorker';
import { countWords, type LoggedPost } from '../lib/content/postPerformance';
import { deriveSignals } from '../lib/content/whatWorks';
import { loadPlaybook } from '../lib/content/playbookLoader';
import {
  deterministicFallbackDraft,
  generateWithWorker,
  shapePrompt,
  toPromptProvenance,
  type ShapedPrompt,
} from '../lib/content/promptShaper';
import {
  backtest,
  lowestDimension,
  scoreAgainst,
} from '../lib/content/scoreCalibration';

const DIM_LABELS: Record<string, string> = {
  hook: 'Hook',
  specificity: 'Specificity',
  cta: 'CTA',
  length: 'Length fit',
};

/**
 * Deterministic single-dimension fix for fallback mode: every transform
 * uses only the draft's own text + the idea — no fabricated facts. The
 * button always does something real, worker or not.
 */
function fallbackFixDimension(dim: string, draft: string, idea: string): string {
  const trimmed = draft.replace(/\s+$/, '');
  if (dim === 'hook') {
    const rest = draft.split('\n').slice(1).join('\n');
    const stem = idea.replace(/[.?!]+\s*$/, '') || 'this';
    return `What actually changes when ${stem}?\n${rest}`.replace(/\s+$/, '');
  }
  if (dim === 'cta') {
    return `${trimmed}\n\nReply and tell me what you would add.`;
  }
  if (dim === 'specificity') {
    // A true, self-derived number — never an invented customer figure.
    return `${trimmed}\n\nFor the record: ${countWords(draft)} words, 1 ask.`;
  }
  if (dim === 'length') {
    const lines = trimmed.split('\n');
    if (countWords(trimmed) <= 120) return trimmed;
    const kept: string[] = [];
    let words = 0;
    for (const line of lines) {
      const w = countWords(line);
      if (words + w > 120 && kept.length > 0) break;
      kept.push(line);
      words += w;
    }
    const last = lines[lines.length - 1];
    if (kept[kept.length - 1] !== last) kept.push('', last);
    return kept.join('\n').replace(/\n{3,}/g, '\n\n');
  }
  return trimmed;
}

export function HistoryShapedPanel({
  posts,
  format,
  workerReady,
}: {
  posts: LoggedPost[];
  format: string;
  workerReady: boolean;
}) {
  const [idea, setIdea] = useState('');
  const [draft, setDraft] = useState('');
  const [shaped, setShaped] = useState<ShapedPrompt | null>(null);
  const [source, setSource] = useState<'worker' | 'fallback' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provOpen, setProvOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const streamTimer = useRef<number | null>(null);

  // Pure derivations — same inputs, same signals/rules/scores.
  const signalSet = useMemo(() => deriveSignals(posts), [posts]);
  const playbook = useMemo(() => loadPlaybook(), []);
  const provenance = useMemo(() => (shaped ? toPromptProvenance(shaped) : null), [shaped]);
  const score = useMemo(
    () => (draft.trim() ? scoreAgainst(draft, signalSet, playbook) : null),
    [draft, signalSet, playbook],
  );
  const lowest = score ? lowestDimension(score.breakdown) : '';
  const calibration = useMemo(
    () => backtest(posts, (p) => scoreAgainst(p.text, signalSet, playbook).overall),
    [posts, signalSet, playbook],
  );

  // Never leave a stream timer running after unmount.
  useEffect(
    () => () => {
      if (streamTimer.current !== null) window.clearTimeout(streamTimer.current);
    },
    [],
  );

  /** Progressive reveal of worker text into the editor. */
  const streamIn = (text: string) => {
    if (streamTimer.current !== null) window.clearTimeout(streamTimer.current);
    const chunks = text.split(/(\s+)/);
    let i = 0;
    setDraft('');
    const step = () => {
      i = Math.min(chunks.length, i + 4);
      setDraft(chunks.slice(0, i).join(''));
      if (i < chunks.length) {
        streamTimer.current = window.setTimeout(step, 24);
      } else {
        streamTimer.current = null;
        setBusy(false);
      }
    };
    step();
  };

  /** Adapter over the real worker seam; undefined when not configured. */
  const worker = workerReady
    ? {
        run: async (prompt: string) =>
          (
            await argoWorker.runAgent(
              {
                id: 'content-history-shaper',
                name: 'History-shaped Content',
                template: 'content',
                persona: 'writer',
              },
              { prompt },
            )
          ).text,
      }
    : undefined;

  const runShaped = async (p: ShapedPrompt, fallback: (sp: ShapedPrompt) => string) => {
    setBusy(true);
    setError(null);
    try {
      const out = await generateWithWorker(p, worker, fallback);
      setSource(out.source);
      if (out.source === 'worker') {
        streamIn(out.text);
      } else {
        setDraft(out.text);
        setBusy(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed.');
      setBusy(false);
    }
  };

  const handleGenerate = async () => {
    const trimmed = idea.trim();
    if (!trimmed || busy) return;
    const next = shapePrompt({
      idea: trimmed,
      format,
      signalSet,
      playbook,
      totalPosts: posts.length,
    });
    setShaped(next);
    await runShaped(next, deterministicFallbackDraft);
  };

  const handleRewriteLowest = async () => {
    if (!shaped || !score || !lowest || busy) return;
    const fixed: ShapedPrompt = {
      ...shaped,
      system:
        shaped.system +
        `\n\nRewrite directive: the previous draft scored lowest on "${lowest}"` +
        ` (${score.breakdown[lowest]}/100). Rewrite it to fix that dimension` +
        ' specifically while keeping everything that already works.',
      user: `${shaped.user}\n\nPrevious draft to rewrite:\n${draft}`,
    };
    const currentDraft = draft;
    const currentIdea = idea.trim();
    await runShaped(fixed, () => fallbackFixDimension(lowest, currentDraft, currentIdea));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — non-fatal.
    }
  };

  const canGenerate = idea.trim().length > 0 && !busy;

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <History size={16} style={{ color: C.magenta, flexShrink: 0 }} />
        <span className="t-section-title" style={{ color: C.text }}>
          History-shaped draft
        </span>
        <span className="t-meta" style={{ color: C.textDim }}>
          your signals + the playbook · provenance receipts · calibrated score
        </span>
        <span
          className="glass-pill"
          title="How much the prompt leans on YOUR logged history vs the playbook"
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            padding: '3px 10px',
            color: C.magentaSoft,
            background: `${C.magentaSoft}18`,
          }}
        >
          {Math.round((shaped?.personalWeight ?? 0) * 100) ||
            Math.round(Math.min(1, posts.length / 20) * 100)}
          % personalized · {posts.length} post{posts.length === 1 ? '' : 's'} logged
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleGenerate();
          }}
          placeholder={`Idea for your next ${format}, shaped by what already works for you`}
          style={{
            flex: 1,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10,
            padding: '10px 12px',
            color: C.text,
            fontSize: 13,
            outline: 'none',
          }}
        />
        <button
          onClick={() => void handleGenerate()}
          disabled={!canGenerate}
          className="glass-btn glass-btn--primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: canGenerate ? 1 : 0.5,
            cursor: canGenerate ? 'pointer' : 'default',
          }}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          Generate
        </button>
      </div>

      {!workerReady && (
        <div className="t-meta" style={{ color: C.textMuted }}>
          Worker not configured — the deterministic template fallback runs instead (labelled on
          the draft). Set VITE_AGENT_API_URL for live generation.
        </div>
      )}

      {error && (
        <div className="t-meta" style={{ color: C.orangeWarm }}>
          {error}
        </div>
      )}

      {/* Signals-used provenance chip — the receipt naming exactly which
          signals + rules shaped this draft (from toPromptProvenance). */}
      {provenance && (
        <div style={{ display: 'grid', gap: 6 }}>
          <button
            onClick={() => setProvOpen((o) => !o)}
            className="glass-pill"
            aria-expanded={provOpen}
            title={`Provenance digest ${provenance.digest}`}
            style={{
              justifySelf: 'start',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              padding: '4px 10px',
              cursor: 'pointer',
              border: 'none',
              color: C.blueElectric,
              background: `${C.blueElectric}18`,
              boxShadow: `inset 0 0 0 1px ${C.blueElectric}44`,
            }}
          >
            <Fingerprint size={12} />
            signals used: {provenance.summary}
            {provOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {provOpen && (
            <div className="t-meta" style={{ color: C.textMuted, display: 'grid', gap: 3 }}>
              {provenance.signalIds.length > 0 ? (
                provenance.signalIds.map((id) => <div key={id}>signal · {id}</div>)
              ) : (
                <div>no personal signals yet — 100% playbook (cold start is honest)</div>
              )}
              {shaped?.injectedRules.map((r) => (
                <div key={r.id}>
                  rule · [{r.id}] {r.title}
                </div>
              ))}
              <div style={{ color: C.textDim }}>receipt digest {provenance.digest}</div>
            </div>
          )}
        </div>
      )}

      {/* The editor the draft streams into. Editable — the score below is live. */}
      {(draft || busy) && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={8}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                padding: 12,
                color: C.text,
                fontFamily: 'monospace',
                fontSize: 12,
                lineHeight: 1.5,
                resize: 'vertical',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {source && (
              <span
                className="glass-pill"
                style={{
                  fontSize: 11,
                  padding: '3px 10px',
                  color: source === 'worker' ? C.green : C.orange,
                  background: source === 'worker' ? `${C.green}18` : `${C.orange}18`,
                }}
              >
                {source === 'worker' ? 'live worker draft' : 'deterministic fallback'}
              </span>
            )}
            {score && (
              <span className="t-meta" style={{ color: C.textMuted }}>
                calibrated score {score.overall}/100
              </span>
            )}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              {score && lowest && !busy && (
                <button
                  onClick={() => void handleRewriteLowest()}
                  className="glass-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                  title={`Weakest dimension: ${DIM_LABELS[lowest] ?? lowest} (${score.breakdown[lowest]}/100)`}
                >
                  <Wand2 size={13} />
                  Rewrite lowest: {DIM_LABELS[lowest] ?? lowest}
                </button>
              )}
              {draft && (
                <button
                  onClick={() => void handleCopy()}
                  className="glass-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                >
                  {copied ? (
                    <Check size={12} style={{ color: C.green }} />
                  ) : (
                    <Copy size={12} style={{ color: C.textMuted }} />
                  )}
                  Copy
                </button>
              )}
            </span>
          </div>

          {/* Live per-dimension calibrated breakdown. */}
          {score && (
            <div style={{ display: 'grid', gap: 5 }}>
              {Object.entries(score.breakdown).map(([dim, value]) => {
                const barColor = value >= 70 ? C.green : value >= 40 ? C.orange : C.orangeWarm;
                return (
                  <div key={dim} style={{ display: 'grid', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span className="t-meta" style={{ color: C.text, fontSize: 12 }}>
                        {DIM_LABELS[dim] ?? dim}
                        {dim === lowest ? ' · weakest' : ''}
                      </span>
                      <span className="t-meta" style={{ marginLeft: 'auto', color: C.textMuted }}>
                        {value}/100
                      </span>
                    </div>
                    <div
                      style={{
                        height: 4,
                        borderRadius: 2,
                        background: 'rgba(255,255,255,0.08)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${value}%`,
                          height: '100%',
                          borderRadius: 2,
                          background: barColor,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Predicted-vs-actual calibration mini panel from backtest(). */}
      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          paddingTop: 10,
          display: 'grid',
          gap: 6,
        }}
      >
        <div className="t-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Activity size={12} style={{ color: C.blueElectric }} />
          Score calibration — predicted vs actual
          {calibration.predictedVsActual.length >= 2 && (
            <span
              className="glass-pill"
              title="Pearson correlation between predicted score and observed engagement"
              style={{
                marginLeft: 'auto',
                fontSize: 11,
                padding: '2px 8px',
                color:
                  calibration.correlation >= 0.5
                    ? C.green
                    : calibration.correlation >= 0
                      ? C.orange
                      : C.orangeWarm,
                background: 'rgba(255,255,255,0.05)',
              }}
            >
              r = {calibration.correlation.toFixed(2)}
            </span>
          )}
        </div>
        {calibration.predictedVsActual.length < 2 ? (
          <div className="t-meta" style={{ color: C.textDim }}>
            Log at least 2 posts with real metrics (panel below) to backtest the scorer against
            ground truth. No posts, no correlation claimed.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 3 }}>
            {calibration.predictedVsActual.slice(-6).map((row) => (
              <div
                key={row.id}
                className="t-meta"
                style={{ display: 'flex', gap: 10, color: C.textMuted }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.id}
                </span>
                <span>predicted {Math.round(row.predicted)}</span>
                <span style={{ color: C.text }}>actual {row.actual.toFixed(1)}% eng</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </GlassPanel>
  );
}
